import type { ComponentPropsWithoutRef, ElementType, ForwardedRef } from "react";
import { type ReactNode } from "react";

import { Spinner } from "../Spinner";
import { ButtonBase, type ButtonBaseProps } from "./ButtonBase";
import type { PositionInButtonGroup } from "./types";
import genericForwardRef from "../generic-forward-ref";
import clsx from "clsx";
import { buttonTheme as theme } from "./theme";
import type { Color, Size } from "../types";

export type ButtonProps<T extends ElementType = "button"> = {
  as?: T | null;
  href?: string;
  color?: Color;
  fullSized?: boolean;
  target?: string;
  isProcessing?: boolean;
  processingLabel?: string;
  processingSpinner?: ReactNode;
  label?: ReactNode;
  outline?: boolean;
  pill?: boolean;
  positionInGroup?: keyof PositionInButtonGroup;
  size?: Size;
  padded?: boolean;
  minimal?: boolean;
  isIconButton?: boolean;
  focusRing?: boolean;
  absolute?: boolean;
  animate?: boolean;
} & ComponentPropsWithoutRef<T>;

const ButtonComponentFn = <T extends ElementType = "button">(
  {
    children,
    className,
    color = "primary",
    disabled,
    fullSized,
    isProcessing = false,
    processingLabel = "Loading...",
    processingSpinner,
    label,
    outline,
    pill = false,
    positionInGroup = "none",
    size = "md",
    padded = true,
    minimal,
    icon,
    focusRing = true,
    absolute = false,
    isIconButton,
    animate = true,
    ...props
  }: ButtonProps<T>,
  ref: ForwardedRef<T>,
) => {
  const theirProps = props as ButtonBaseProps<T>;

  const getThemeColor = () => {
    if (outline) {
      return theme.outline.color[color] ?? theme.outline.color.primary;
    }
    if (minimal) {
      return theme.color[color].base;
    }
    return clsx(theme.color[color].base, theme.color[color].background, theme.color[color].border);
  };

  return (
    <ButtonBase
      ref={ref}
      disabled={disabled}
      className={clsx(
        theme.base,
        animate && theme.animate,
        padded && theme.padded,
        disabled && theme.disabled,
        getThemeColor(),
        isProcessing && theme.isProcessing,
        theme.pill[pill ? "on" : "off"],
        fullSized && theme.fullSized,
        focusRing && theme.focusRing,
        absolute ? "absolute" : "relative",
        isIconButton && pill && clsx(theme.iconButton.size[size]),
        className,
      )}
      {...theirProps}
    >
      <span
        className={clsx(
          theme.inner.base,
          animate && theme.animate,
          theme.outline[outline ? "on" : "off"],
          theme.outline.pill[outline && pill ? "on" : "off"],
          isIconButton
            ? clsx(theme.iconButton.size[size], "items-center justify-center")
            : theme.size[size],
          isIconButton && theme.pill[pill ? "on" : "off"],
          isProcessing && !isIconButton && theme.inner.isProcessingPadding[size],
          theme.inner.position[positionInGroup],
        )}
      >
        <>
          {isProcessing && (
            <span
              className={clsx(theme.spinnerSlot, !isIconButton && theme.spinnerLeftPosition[size])}
            >
              {processingSpinner || <Spinner size={size} buttonSpinner />}
            </span>
          )}
          {typeof children !== "undefined" ? (
            children
          ) : (
            <span data-testid="flowbite-button-label" className={clsx(theme.label)}>
              {isProcessing ? processingLabel : label}
            </span>
          )}
        </>
      </span>
    </ButtonBase>
  );
};

ButtonComponentFn.displayName = "Button";

const ButtonComponent = genericForwardRef(ButtonComponentFn);

export const Button = Object.assign(ButtonComponent, {});
